import React, { useRef } from 'react';
import {
  Datagrid,
  FunctionField,
  ResourceContextProvider,
  TextField,
  useRefresh,
} from 'react-admin';

import Chip from '@material-ui/core/Chip';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';

import ActionsList from '../../Components/ReactAdmin/ActionsList';
import ActionsField from '../../Components/ReactAdmin/Fields/ActionsField';
import ListPageTabbed from '../../Components/ReactAdmin/List/ListPageTabbed';
import { ProtegerPagina } from '../../layout/VerificaPermissaoPage';
import { PRIMARY_COLOR } from '../../utils';
import { STATUS_ATIVO } from '../../utils/constants';
import { PERMISSAO_TELA_EXPOSICAO_LOJA_LISTAR } from '../../utils/dadosTelas';
import DialogoEditarProdutoExposicao from './DialogoEditarProdutoExposicao';
import Filter from './Filter';

function getLabelRepeticao(repeticao) {
  if (repeticao === 1) return 'Única';
  if (repeticao === 2) return 'Duplicado';
  if (repeticao === 3) return 'Triplicado';
  if (repeticao > 3) return 'Mais de 3';
  return 'NÃO EXPOSTO';
}

function getCoresRepeticao(repeticao) {
  if (repeticao === 1)
    return { bg: '#e8f5e9', borda: '#43a047', texto: '#2e7d32' };
  if (repeticao === 2)
    return { bg: '#e3f2fd', borda: '#1e88e5', texto: '#1565c0' };
  if (repeticao === 3)
    return { bg: '#fff3e0', borda: '#fb8c00', texto: '#e65100' };
  if (repeticao > 3)
    return { bg: '#f3e5f5', borda: '#8e24aa', texto: '#6a1b9a' };
  return { bg: '#fdecea', borda: '#d32f2f', texto: '#d32f2f' };
}

const useStyles = makeStyles((theme) => ({
  dataHeader: {
    flex: 1,
    backgroundColor: PRIMARY_COLOR,
    padding: '10px',
    color: 'white',
    fontWeight: 500,
    lineHeight: '1.5rem',
    fontSize: '0.875rem',
    borderBottom: '1px solid rgba(224, 224, 224, 1)',
    borderSpacing: 0,
    borderCollapse: 'collapse',
    '& .MuiTableSortLabel-root.MuiTableSortLabel-active': {
      color: 'red',
    },
    '& .MuiTableSortLabel-root.MuiTableSortLabel-active .MuiTableSortLabel-icon': {
      color: 'red !important',
    },
  },
  dataCell: {
    flex: 1,
    padding: '10px',
    fontWeight: 500,
    lineHeight: '1.5rem',
    fontSize: '0.875rem',
    borderSpacing: 0,
    borderCollapse: 'collapse',
  },
}));

const List = (props) => {
  const refresh = useRefresh();
  const classes2 = useStyles();
  const refDialogoEditarProdutoExposicao = useRef(null);

  function handleEdit({ id }) {
    if (refDialogoEditarProdutoExposicao.current) {
      refDialogoEditarProdutoExposicao.current.handleOpen(id);
    }
  }

  const toolbarActions = <ActionsList {...props} />;

  const renderGrid = ({ listProps, classes, listContext }) => (
    <Datagrid {...props} resource="enderecos_exposicao_mercadorias">
      <TextField
        source="codigo"
        label="Descrição"
        headerClassName={classes2.dataHeader}
        cellClassName={classes2.dataCell}
      />
      <FunctionField
        source="produto_terreo"
        label="Produto"
        headerClassName={classes2.dataHeader}
        cellClassName={classes2.dataCell}
        render={(record) => {
          const ocupanteTerreo = (record.andaresProdutos || []).find(
            (ap) => ap.andar === 1,
          );
          if (ocupanteTerreo && ocupanteTerreo.produto) {
            return ocupanteTerreo.produto.nome;
          }
          return (
            <Typography
              variant="body2"
              style={{ fontStyle: 'italic', color: '#9e9e9e' }}
            >
              Vazio
            </Typography>
          );
        }}
      />
      <FunctionField
        source="exposicao"
        label="Exposição"
        headerClassName={classes2.dataHeader}
        cellClassName={classes2.dataCell}
        sortable={false}
        render={(record) => {
          const ocupanteTerreo = (record.andaresProdutos || []).find(
            (ap) => ap.andar === 1,
          );
          const repeticao =
            ocupanteTerreo && ocupanteTerreo.produto
              ? ocupanteTerreo.produto.repeticao_exposicao_na_loja
              : 0;
          const cores = getCoresRepeticao(repeticao);
          return (
            <Chip
              size="small"
              label={getLabelRepeticao(repeticao)}
              style={{
                backgroundColor: cores.bg,
                color: cores.texto,
                border: `1px solid ${cores.borda}`,
                fontWeight: 600,
              }}
            />
          );
        }}
      />
      <ActionsField
        textAlign="right"
        label="Ações"
        sortable={false}
        headerClassName={classes2.dataHeader}
        cellClassName={classes2.dataCell}
        actions={[
          {
            title: 'Editar',
            icon: <EditOutlinedIcon />,
            show: () => true,
            onClick: (r) => handleEdit(r),
          },
        ]}
      />
    </Datagrid>
  );

  return (
    <ProtegerPagina permissao={PERMISSAO_TELA_EXPOSICAO_LOJA_LISTAR}>
      {/* Esta tela é uma cópia de "Endereços" que aponta pro mesmo recurso
      do backend — o ResourceContextProvider força o resource correto
      (enderecos_exposicao_mercadorias) mesmo essa tela estando registrada
      sob a rota/nome "exposicao_loja", que não existe no backend. */}
      <ResourceContextProvider value="enderecos_exposicao_mercadorias">
        <ListPageTabbed
          title="Exposição em Loja"
          filters={<Filter />}
          toolbarActions={toolbarActions}
          renderGrid={renderGrid}
          perPage={50}
          filterDefaultValues={{ status: STATUS_ATIVO }}
          listProps={{
            sort: { field: 'codigo', order: 'asc' },
            ...props,
            resource: 'enderecos_exposicao_mercadorias',
            // Filtro fixo: só entram aqui os endereços com o térreo marcado
            // como exposição em loja — diferente da tela "Endereços", que
            // mostra todos. Fica sempre aplicado, independente de aba/filtro.
            filter: { terreo_exposicao: true },
          }}
        />

        <DialogoEditarProdutoExposicao
          ref={refDialogoEditarProdutoExposicao}
          handleClose={refresh}
        />
      </ResourceContextProvider>
    </ProtegerPagina>
  );
};

export default List;
